import React from 'react';
import { toPng } from 'html-to-image';
import { orderBy } from 'lodash';

import { BlogOrEmailContent, MediaSubType, Studio } from '../types.ts/general';
import {
  Artifact,
  ContentViewData,
  FileData,
  OtherVideo,
  PhotoArtifactTab,
  PhotoAsset,
  Story,
} from '../types.ts/story';
import TurndownService from 'turndown';
import { v4 as uuid } from 'uuid';
import { AssetRepository } from '../repositories/AssetRepository';
import { SimpleSchemaTypes } from '@datocms/cma-client-browser';
import { AspectRatio } from '../types.ts/video';
import VideoCreatorStore from '../stores/VideoCreatorStore';
import { AlbumRepository, StoryRepository } from '../repositories';

const turndownService = new TurndownService();

export function delay(t: number) {
  return new Promise(function (resolve) {
    setTimeout(resolve, t);
  });
}

export function retry<T>(
  fn: () => Promise<T>,
  retries = 3,
  delayTime = 2000,
  err?: Error,
): Promise<T> {
  if (!retries) {
    return Promise.reject(err);
  }

  return delay(delayTime).then(() =>
    fn().catch((err: Error) => {
      return retry(fn, retries - 1, delayTime * 2, err);
    }),
  );
}

export function chunkArray<T>(array: T[], chunkSize: number): T[][] {
  return Array.from({ length: Math.ceil(array.length / chunkSize) }, (_, i) =>
    array.slice(i * chunkSize, i * chunkSize + chunkSize),
  );
}

export function randomString(length: number) {
  let result = '';
  const characters =
    'ABCDEFGHIJKLMNOPQRSTUVWXYZabcdefghijklmnopqrstuvwxyz0123456789';
  const charactersLength = characters.length;
  let counter = 0;
  while (counter < length) {
    result += characters.charAt(Math.floor(Math.random() * charactersLength));
    counter += 1;
  }
  return result;
}

export const handleCopyToClipboard = async (text: string, format = 'plain') => {
  try {
    const clipboardItem = new ClipboardItem({
      [`text/${format}`]: new Blob([text], { type: `text/${format}` }),
    });

    await navigator.clipboard.write([clipboardItem]);
  } catch (err) {}
};

export function getRandomFileName(inputString: string) {
  if (!inputString) return uuid();
  const words = inputString.split(/\s+/);
  const filteredWords = words.filter((word) => word.length > 4);

  const numberOfWords = filteredWords.length >= 3 ? 3 : 2;

  if (filteredWords.length < numberOfWords) {
    return inputString;
  }

  const randomIndices: number[] = [];

  while (randomIndices.length < numberOfWords) {
    const randomIndex = Math.floor(Math.random() * filteredWords.length);

    if (!randomIndices.includes(randomIndex)) {
      randomIndices.push(randomIndex);
    }
  }

  const randomWords = randomIndices.map((index) => filteredWords[index]);
  const randomNumber = Math.floor(Math.random() * 1000);

  return `${randomWords.join('_')}_${randomNumber}`;
}

export async function handleDownloadMedia<
  T extends { url: string; fileName?: string; id?: string },
  E extends HTMLElement,
>(e: React.MouseEvent<E, MouseEvent>, media: T) {
  e.preventDefault();
  e.stopPropagation();
  try {
    const url = media.url;
    let blob: Blob;
    let mimeType: string;

    // handle base64 data
    if (url.startsWith('data:')) {
      const response = await fetch(url);
      blob = await response.blob();
      mimeType = blob.type;
    } else {
      // handle regular URLs
      const extension = url.split('.').pop()?.toLowerCase();
      const mimeTypes: Record<string, string> = {
        mp4: 'video/mp4',
        webm: 'video/webm',
        mov: 'video/quicktime',
        jpg: 'image/jpeg',
        jpeg: 'image/jpeg',
        png: 'image/png',
        gif: 'image/gif',
        heic: 'image/heic',
        heif: 'image/heif',
        webp: 'image/webp',
      };
      const response = await fetch(url);
      const responseBlob = await response.blob();
      mimeType =
        extension && mimeTypes[extension]
          ? mimeTypes[extension]
          : responseBlob.type;
      blob = new Blob([responseBlob], { type: mimeType });
    }

    let fileName = media.fileName || media.id || 'download';
    const mimeToExt: Record<string, string> = {
      'image/png': '.png',
      'image/jpeg': '.jpg',
      'image/gif': '.gif',
      'image/heic': '.heic',
      'image/heif': '.heif',
      'image/webp': '.webp',
      'video/mp4': '.mp4',
      'video/webm': '.webm',
      'video/quicktime': '.mov',
    };
    const ext = mimeToExt[mimeType];
    if (ext && !fileName.toLowerCase().endsWith(ext)) {
      fileName += ext;
    }

    const link = document.createElement('a');
    link.href = globalThis.URL.createObjectURL(blob);
    link.download = fileName;

    document.body.appendChild(link);
    link.click();
    document.body.removeChild(link);
    globalThis.URL.revokeObjectURL(link.href);
  } catch (error) {
    console.error('Download failed:', error);
  }
}

export const getPosterUrl = (
  url: string,
  width: number,
  height: number,
  fit = true,
) => {
  if (!url) return;

  const imgixParts = {
    w: width,
    h: height,
    ...(fit && { fit: 'crop' }),
    q: 95,
    // fit: 'fill',
    // fill: 'blur',
  };
  return (
    url +
    '?' +
    Object.entries(imgixParts)
      .map(([k, v]) => `${k}=${v}`)
      .join('&')
  );
};

export const shouldWhitelist = (
  flags: (boolean | string)[],
  enterprisePlatform: string[],
) => {
  //const showcases = videoCreator.story?._allReferencingShowcases;

  //const showcaseIds = showcases?.map((s) => s.id) || [];
  return flags?.some((s) => s) || enterprisePlatform?.some((s) => s) || false;
};

export type SortBy =
  | 'createdAt' // oldest to newest
  | '-createdAt'; // newest to oldest

export const sortPhotoAssets = (
  assets: PhotoAsset[] | undefined,
  sort: SortBy,
) => {
  if (!assets) return [];
  return [...assets].sort((a, b) => {
    const dateA = new Date(a._createdAt || 0).getTime();
    const dateB = new Date(b._createdAt || 0).getTime();
    return sort.startsWith('-') ? dateB - dateA : dateA - dateB;
  });
};

export async function handleRemoveMedia<
  T extends PhotoArtifactTab | Omit<MediaSubType, MediaSubType.all>,
>(
  videoCreator: VideoCreatorStore,
  albumRepository: AlbumRepository,
  mediaId: string,
  type: T,
  toggleDropdown = (e: boolean) => {},
  sortBy = '-createdAt' as SortBy,
) {
  try {
    let artifacts = videoCreator.story?.storyArtifacts || [];
    let assets = videoCreator.story?.storyAssets || [];
    let aiPhotos = videoCreator.story?.aiPhotos || [];
    let videoArtifacts = videoCreator.story?.storyArtifactsVideo || [];
    let albumIndex;
    let resource;

    const narrowedType = type as unknown as PhotoArtifactTab | MediaSubType;

    switch (narrowedType) {
      case PhotoArtifactTab.story:
      case MediaSubType.artifact:
        artifacts = artifacts.filter((artifact) => artifact.id !== mediaId);
        resource = artifacts;
        break;
      case PhotoArtifactTab.album:
        const albumIdx =
          videoCreator.story?._allReferencingShowcases?.findIndex(
            (a) => a.organizationArtifacts?.some((v) => v.id === mediaId),
          );
        if (albumIdx !== undefined && albumIdx > -1) {
          videoCreator.story!._allReferencingShowcases[
            albumIdx
          ].organizationArtifacts =
            videoCreator.story!._allReferencingShowcases[
              albumIdx
            ].organizationArtifacts?.filter((a) => a.id !== mediaId) || [];
          albumIndex = albumIdx;
        }
        resource =
          videoCreator?.story?._allReferencingShowcases?.flatMap(
            (album) => album.organizationArtifacts || [],
          ) || [];
        break;
      case PhotoArtifactTab.logo: {
        const albumIdx =
          videoCreator.story?._allReferencingShowcases?.findIndex(
            (a) => a.organizationLogos?.some((v) => v.id === mediaId),
          );
        if (albumIdx !== undefined && albumIdx > -1) {
          videoCreator.story!._allReferencingShowcases[
            albumIdx
          ].organizationLogos =
            videoCreator.story!._allReferencingShowcases[
              albumIdx
            ].organizationLogos?.filter((a) => a.id !== mediaId) || [];
          albumIndex = albumIdx;
        }
        resource =
          videoCreator?.story?._allReferencingShowcases?.flatMap(
            (album) => album.organizationLogos || [],
          ) || [];
        break;
      }
      case PhotoArtifactTab.stock:
      case MediaSubType.stock:
        assets = assets.filter((asset) => asset.id !== mediaId);
        resource = assets;
        break;
      case MediaSubType.ai:
      case PhotoArtifactTab.ai:
        aiPhotos = aiPhotos.filter((ai) => ai.id !== mediaId);
        resource = aiPhotos;
        break;
      case MediaSubType.video:
        videoArtifacts = videoArtifacts.filter((vi) => vi.id !== mediaId);
        resource = videoArtifacts;
        break;
      default:
        return;
    }

    videoCreator.story! = {
      ...videoCreator.story!,
      storyArtifacts: artifacts,
      storyAssets: assets,
      storyArtifactsVideo: videoArtifacts,
      aiPhotos,
    };

    const photoResource = sortPhotoAssets(
      resource
        ?.filter((a) => a.responsiveImage)
        .map((a) => ({
          id: a.id,
          title: a.title,
          src: a.responsiveImage?.src,
          type: 'artifact',
          _createdAt: a._createdAt,
          ...a.customData,
        })) as PhotoAsset[],
      sortBy,
    );

    toggleDropdown(false);
    videoCreator.selectedPhotoAssets = {
      tab: type as PhotoArtifactTab,
      resource: photoResource,
      lastSelectedStock: videoCreator.selectedPhotoAssets.lastSelectedStock,
      lastSelectedAi: videoCreator.selectedPhotoAssets.lastSelectedAi,
      selectedId: videoCreator.selectedPhotoAssets.selectedId,
    };
    if (type === PhotoArtifactTab.album || type === PhotoArtifactTab.logo) {
      if (albumIndex !== undefined && albumIndex > -1) {
        await albumRepository?.update(
          videoCreator.story!._allReferencingShowcases[albumIndex]!,
        );
      }
    } else {
      await videoCreator.updateStory(videoCreator.story!);
    }
  } catch (error) {
  } finally {
    toggleDropdown(false);
  }
}

export const handleLoadGeneratedContent = (
  type: 'blog' | 'socialPost',
  videoCreator: VideoCreatorStore,
  content: BlogOrEmailContent,
  setCurrentContent: (content: BlogOrEmailContent | null) => void,
) => {
  setCurrentContent(content);
  let markdownContent = turndownService.turndown(content.content);
  const text = markdownContent
    .replace(/Replace image\n\nDelete image\n\n/g, '')
    .replace(/Add Photo\n\nDelete\n\n/g, '');

  const generatedContent = videoCreator.contentStudioGeneratedContent;
  const contentKey = type === 'blog' ? 'Blog' : 'Social Post';

  videoCreator.contentStudioGeneratedContent = {
    ...(generatedContent || {}),
    [contentKey]: {
      ...(generatedContent?.[contentKey] || {}),
      content: {
        ...(generatedContent?.[contentKey]?.content || {}),
        title: content.title,
        response: text,
      },
    },
  } as ContentViewData;

  videoCreator.selectedGeneratedContent[type] = {
    id: content.id.toString(),
    content: content.title,
    type: 'saved',
  };
};

export async function handleDeleteBlogContent(
  videoCreator: VideoCreatorStore,
  storyRepository: StoryRepository,
  contentId: number,
  currentContentId: number | undefined,
  setOptionsModal: (e: null) => void,
  setDropdownOpen: (e: boolean) => void,
  setCurrentContent: (e: BlogOrEmailContent | null) => void,
  type: 'savedBlog' | 'savedSocialPost' = 'savedBlog',
  saveType: 'saved_blog' | 'saved_social_post' = 'saved_blog',
) {
  setOptionsModal(null);
  setDropdownOpen(false);
  const generatedContentType = type === 'savedBlog' ? 'blog' : 'socialPost';
  videoCreator.selectedGeneratedContent[generatedContentType] = null;

  const content = videoCreator.story?.[type];
  if (!content?.hasOwnProperty(contentId)) return;

  delete videoCreator.story?.[type]?.[contentId];
  if (contentId === currentContentId) {
    videoCreator.selectedGeneratedContent[generatedContentType] = null;
    setCurrentContent(null);
  }

  await storyRepository?.handleSaveAiGeneratedContent(
    videoCreator.story!.id,
    videoCreator.story?.[type]!,
    saveType,
  );
}

export async function handleRenameGeneratedContent(
  videoCreator: VideoCreatorStore,
  storyRepository: StoryRepository,
  contentId: number,
  title: string,
  type: 'savedBlog' | 'savedSocialPost',
  saveType: 'saved_blog' | 'saved_social_post',
  toggleContentToEditModal: (e: null) => void,
  setOptionsModal: (e: null) => void,
  setDropdownOpen: (e: boolean) => void,
  setCurrentContent: (e: BlogOrEmailContent | null) => void,
  loadContent: boolean = true,
) {
  toggleContentToEditModal(null);
  setOptionsModal(null);
  setDropdownOpen(false);

  // Need to use any type since the exact shape is not fully known
  const data = videoCreator.story![type] as any;
  if (!data || !Object.keys(data).length) return;

  if (data.hasOwnProperty(contentId.toString())) {
    data[contentId].title = title;
  } else {
    // Safe mutation of the data
    data[contentId] = {
      ...(data[contentId] || { content: '' }),
      title,
    };
  }

  if (loadContent) {
    handleLoadGeneratedContent(
      type === 'savedBlog' ? 'blog' : 'socialPost',
      videoCreator,
      {
        id: contentId,
        title: title,
        content: data[contentId].content,
        username: data[contentId].username,
      },
      setCurrentContent,
    );
  }

  await storyRepository?.handleSaveAiGeneratedContent(
    videoCreator.story!.id,
    videoCreator.story?.[type]!,
    saveType,
  );
}

export const getCurrentStudio = (pathname: string) => {
  switch (pathname.split('/').join('')) {
    case Studio.content:
      return Studio.content;
    case Studio.creator:
      return Studio.creator;
    case Studio.dashboard:
      return Studio.dashboard;
    case Studio.brandKit:
      return Studio.brandKit;
    default:
      return null;
  }
};

export const initializeContentStudioContent = (
  videoCreator: VideoCreatorStore,
  storyId: string | undefined,
  title: 'Blog' | 'Social Post' | 'Quotes' | 'Email',
) => {
  if (!storyId) return;
  const contentGenerated = videoCreator.contentStudioGeneratedContent;
  if (contentGenerated?.[title]?.storyId === storyId) return;

  videoCreator.contentStudioGeneratedContent = {
    ...(contentGenerated || {}),
    [title]: {
      storyId,
      hasBeenGenerated: false,
      usedSpeakers: false,
      content: {
        title: '',
        response: '',
      },
    },
  } as ContentViewData;
};

export const getAvailableClipFormats = (video: OtherVideo) => {
  const formats =
    [video, ...(video.associatedVideos || [])].map((v) => ({
      aspectRatio: (v.aspectRatio || '') as string,
      isPublished: !!v.videoFilePrimary,
    })) || [];

  const availableFormats = formats.filter(
    (format, index) =>
      formats.findIndex((f) => f.aspectRatio === format.aspectRatio) === index,
  );

  return orderBy(availableFormats, ['aspectRatio'], ['asc']);
};

// function that will give two significant figures and K for thousands and M for millions to numbers
export function formatAnalyticsNumber(num: number) {
  if (num >= 1000000) {
    return (num / 1000000).toFixed(2).replace(/\.0$/, '') + 'M';
  }
  if (num >= 10000) {
    return (num / 1000).toFixed(1).replace(/\.0$/, '') + 'K';
  }
  if (num >= 1000) {
    return (num / 1000).toFixed(2).replace(/\.0$/, '') + 'K';
  }
  return num;
}

export const createImageElement = async (
  baseElement: HTMLElement,
  backgroundImage: string,
  quote: string,
  cb: () => void,
  aspectRatio = AspectRatio.AR_16_9,
) => {
  const placeholderContainer = document.createElement('div');
  const container = document.createElement('div');
  const imageContainer = document.createElement('div');

  placeholderContainer.id = 'placeholder-shareable-image';
  const computedStyles = getComputedStyle(baseElement);
  const heightTags = ['min-block-size', 'min-height', 'height'];
  const widthTags = ['max-inline-size', 'max-width', 'width'];

  const dimension = getShareableImageDimension(aspectRatio);
  const width = `${dimension.width / 2}px`;
  const height = `${dimension.height / 2}px`;

  for (let i = 0; i < computedStyles.length; i++) {
    const styleName = computedStyles[i];
    let value = computedStyles.getPropertyValue(styleName);
    // if (widthTags.includes(styleName)) value = width;
    if (heightTags.includes(styleName)) value = height;
    if (styleName.includes('color')) continue;
    if (styleName === 'background-image') {
      value = `linear-gradient(0deg, rgba(0, 0, 0, 0.8) 0%, rgba(0, 0, 0, 0) 100%), url("${backgroundImage}")`;
    }

    if (styleName === 'background-size') {
      value = 'cover';
    }

    if (styleName === 'background-position') {
      value = 'top';
    }

    if (styleName === 'background-repeat') {
      value = 'no-repeat';
    }

    if (styleName === 'background-color') {
      value = 'rgba(0, 0, 0, 0)';
    }
    imageContainer.style.setProperty(styleName, value);
  }

  imageContainer.style.borderRadius = '0px';
  imageContainer.style.aspectRatio = `${aspectRatio.split(':').join('/')}`;

  // placeholderContainer.style.width = width;
  // placeholderContainer.style.height = height;
  placeholderContainer.style.display = 'flex';
  placeholderContainer.style.justifyContent = 'center';
  placeholderContainer.style.alignItems = 'center';
  placeholderContainer.style.width = '666px';
  placeholderContainer.style.height = '500px';

  // container.style.width = width;
  // container.style.height = height;

  placeholderContainer.style.position = 'fixed';
  placeholderContainer.style.top = '50%';
  placeholderContainer.style.left = '50%';
  placeholderContainer.style.transform = 'translate(-50%, -50%)';

  document.body.appendChild(placeholderContainer);
  placeholderContainer.appendChild(container);
  container.appendChild(imageContainer);

  imageContainer.innerHTML = baseElement.innerHTML;

  const quoteElement = imageContainer.querySelector(
    '.quote-element',
  ) as HTMLElement;
  if (quoteElement) {
    quoteElement.innerHTML = quote;
    quoteElement.style.color = '#fff';
  }

  const svgElement = imageContainer.querySelector('svg');
  if (svgElement) {
    const pathElements = svgElement.querySelectorAll('path');
    pathElements.forEach((path) => {
      path.style.fill = '#fff';
    });
  }

  // Just logs
  const rect = container.getBoundingClientRect();
  console.log('rect', rect, '-- aspect ratio --', rect.width / rect.height);

  cb();
  const blobUrl = await toPng(container);
  placeholderContainer.remove();
  return blobUrl;
};

export const getShareableImageDimension = (currDimension: string) => {
  const width = 1060;
  const height = 1000;

  switch (currDimension) {
    case AspectRatio.AR_1_1:
      return { width, height };
    case AspectRatio.AR_9_16:
      return { width: (width * 9) / 16, height };
    case AspectRatio.AR_16_9:
      return { width: (width * 16) / 9, height };
    default:
      return { width, height };
  }
};

export const addUploadToStory = async (
  videoCreator: VideoCreatorStore,
  assetRepository: AssetRepository,
  newPhotoData: FileData & { fileName: string },
) => {
  const upload = await assetRepository?.uploadFile(newPhotoData);
  if (!upload) return newPhotoData.url!;
  const assetKey = newPhotoData.type === 'stock' ? 'storyAssets' : 'aiPhotos';
  const assets = [
    ...(videoCreator.story?.[assetKey] || []),
    {
      id: upload.id,
      title: newPhotoData.title,
      customData: {},
      format: upload.format,
      mimeType: upload.mime_type,
      url: upload.url,
      responsiveImage: {
        srcSet: upload.url,
        src: upload.url,
        alt: newPhotoData.alt,
        title: newPhotoData.title,
      },
      video: null,
      _createdAt: new Date(),
    },
  ] as Artifact[];

  videoCreator.story![assetKey] = assets;
  await videoCreator.updateStory(videoCreator.story!);
  return upload.url;
};

export const saveAssetToDato = async (
  videoCreator: VideoCreatorStore,
  assetRepository: AssetRepository,
  selectedImage: {
    url: string;
    type: 'stock' | 'ai' | 'artifact' | 'quotes';
    description?: string;
  },
) => {
  let url = selectedImage.url;
  const isInDato = selectedImage.url.startsWith(
    'https://www.datocms-assets.com',
  );

  if (!isInDato) {
    videoCreator.savingStockPhoto = true;
    const fileName = uuid();

    const newPhotoData: FileData & { fileName: string } = {
      type: selectedImage.type,
      url: selectedImage.url,
      fileName,
      alt: selectedImage.description || '',
      title: selectedImage.description || '',
    };
    const imageUrl = await addUploadToStory(
      videoCreator,
      assetRepository,
      newPhotoData,
    );
    url = imageUrl;
    videoCreator.savingStockPhoto = false;
  }
  return url;
};

export const getDocumentHeight = () =>
  Math.max(
    document.body.scrollHeight,
    document.documentElement.scrollHeight,
    document.body.offsetHeight,
    document.documentElement.offsetHeight,
    document.body.clientHeight,
    document.documentElement.clientHeight,
  );

export const uploadFilesAndAsset = async (
  assetRepository: AssetRepository,
  files: (File & {
    preview?: string;
    id?: string;
    author?: string;
  })[],
  contributor: string,
  story: Story,
  cb: (
    file: File & { preview: string; id: string; description: string },
    upload: SimpleSchemaTypes.Upload,
  ) => void = () => {},
) => {
  const uploadResults = await Promise.all(
    files.map(async (file) => {
      const namesplit = file?.name?.split('.');
      const fileName = namesplit?.length
        ? namesplit[0]
        : window.crypto.randomUUID();
      const newFileData = {
        type: 'artifact' as 'artifact',
        file,
        fileName,
        author: file.author,
        alt: '',
        title: '',
        tags: contributor ? [contributor] : undefined,
        customData: { contributor },
      };
      const newUpload = await assetRepository?.uploadFile(newFileData);
      cb(
        file as File & { preview: string; id: string; description: string },
        newUpload!,
      );
      return newUpload;
    }),
  );

  const photoArtifactsIds = story?.storyArtifacts?.map((a) => a.id) || [];
  const videoArtifactsIds = story?.storyArtifactsVideo?.map((a) => a.id) || [];

  uploadResults.forEach((upload: any) => {
    if (upload) {
      if (upload.is_image) {
        photoArtifactsIds.push(upload.id);
      } else {
        videoArtifactsIds.push(upload.id);
      }
    }
  });
  console.log('videoArtifactsIds', videoArtifactsIds);

  story.storyArtifacts = photoArtifactsIds as unknown as Artifact[];
  story.storyArtifactsVideo = videoArtifactsIds as unknown as Artifact[];

  return story;
};

export const formatUploadToArtifact = (
  upload: SimpleSchemaTypes.Upload,
  data: { title?: string; alt?: string },
) => {
  return {
    id: upload.id,
    title: data.title || upload.default_field_metadata.en.title,
    customData: upload.default_field_metadata.en.custom_data,
    format: upload.format,
    mimeType: upload.mime_type,
    width: upload.width?.toString(),
    height: upload.height?.toString(),
    url: upload.url,
    ...(upload.is_image
      ? {
          responsiveImage: {
            srcSet: upload.url,
            src: upload.url,
            alt: data.alt || upload.default_field_metadata.en.alt,
            title: data.title || upload.default_field_metadata.en.title,
            base64: '',
            sizes: '',
          },
        }
      : null),
    video: null,
    _createdAt: upload.created_at || new Date().toISOString(),
  } as Artifact;
};

export const FONTS = [
  'Abel',
  'Anton',
  'Archivo Black',
  'Arimo',
  'Barlow',
  'Bebas Neue',
  'Bitter',
  'Cabin',
  'Caveat',
  'Chakra Petch',
  'Cinzel',
  'Courgette',
  'Dancing Script',
  'DM Sans',
  'Dosis',
  'EB Garamond',
  'Fira Sans',
  'Great Vibes',
  'Hind Siliguri',
  'IBM Plex Sans',
  'Inconsolata',
  'Indie Flower',
  'Inter',
  'Josefin Sans',
  'Jost',
  'Kalam',
  'Kanit',
  'Karla',
  'Lato',
  'Libre Baskerville',
  'Libre Franklin',
  'Lobster',
  'Lora',
  'Manrope',
  'Merienda',
  'Merriweather',
  'Montserrat',
  'Mukta',
  'Mulish',
  'Nanum Gothic',
  'Neuton',
  'Noto Sans',
  'Nunito',
  'Nunito Sans',
  'Open Sans',
  'Oswald',
  'Outfit',
  'Pacifico',
  'Permanent Marker',
  'Playfair Display',
  'Poppins',
  'Proxima Nova',
  'PT Sans',
  'PT Sans Narrow',
  'PT Serif',
  'Quicksand',
  'Raleway',
  'Roboto',
  'Roboto Condensed',
  'Roboto Mono',
  'Roboto Slab',
  'Rubik',
  'Sacramento',
  'Satisfy',
  'Shadows Into Light',
  'Slabo',
  'Source Code Pro',
  'Space Grotesk',
  'Special Gothic Expanded One',
  'Titillium Web',
  'Ubuntu',
  'Work Sans',
];

export const FONT_SIZE_VALUES = [
  12, 14, 16, 18, 20, 22, 24, 28, 30, 32, 36, 40, 48, 60, 72, 96,
];

export const checkWordsInString = (str: string, search: string) => {
  const lowercaseStr = str.toLowerCase();
  const wordsInSearch = (search.toLowerCase().match(/\w+/g) || []) as string[];
  // const wordsInStr = (lowercaseStr.match(/\w+/g) || []) as string[];

  for (let word of wordsInSearch) {
    if (!lowercaseStr.includes(word)) return false;
  }
  return true;
};

export const scrollIfNotInView = (
  dropdown: Element | null,
  container: Element | null,
  extraScroll = 100,
) => {
  if (!dropdown || !container) return;

  const containerRect = container.getBoundingClientRect();
  const elementRect = dropdown.getBoundingClientRect();
  const intoView =
    container.scrollTop + elementRect.bottom - containerRect.bottom;

  if (elementRect.bottom > containerRect.bottom - extraScroll) {
    container.scrollTo({
      left: 0,
      top: intoView + extraScroll,
      behavior: 'smooth',
    });
  }
};

export const customWebFonts = [
  {
    family: 'Proxima Nova',
    weight: 400,
    style: 'normal',
    source:
      'https://www.datocms-assets.com/99106/1723563406-proximanova_light.otf',
  },
  {
    family: 'Proxima Nova',
    weight: 500,
    style: 'normal',
    source:
      'https://www.datocms-assets.com/99106/1723563409-proximanova_regular.ttf',
  },
  {
    family: 'Proxima Nova',
    weight: 600,
    style: 'normal',
    source:
      'https://www.datocms-assets.com/99106/1723562352-proximanova_bold.otf',
  },
  {
    family: 'Proxima Nova',
    weight: 600,
    style: 'italic',
    source:
      'https://www.datocms-assets.com/99106/1723562359-proximanova_boldit.otf',
  },
  {
    family: 'Proxima Nova',
    weight: 700,
    style: 'normal',
    source:
      'https://www.datocms-assets.com/99106/1723563234-proximanova_extrabold.otf',
  },
  {
    family: 'Special Gothic Expanded One',
    weight: 400,
    style: 'normal',
    source:
      'https://www.datocms-assets.com/99106/1745607591-specialgothicexpandedone-regular.ttf',
  },
];

/**
 * Ensures that all elements in a source have z_index values that match their track numbers
 * This helps maintain proper stacking order in the visual display
 *
 * @param source The source object containing elements array
 * @returns The source object with z_index values added to elements
 */
export function ensureElementZPositions(source: any): any {
  // This function name stays the same for backward compatibility, but it sets z_index values
  if (!source || !source.elements || !Array.isArray(source.elements)) {
    return source;
  }

  // Process each element in the source
  const updatedElements = source.elements.map((element: any) => {
    if (!element) return element;

    // Set z_index based on track if it doesn't have one
    let updatedElement = { ...element };

    // If element has a track number, set z_index to match
    if (updatedElement.track !== undefined) {
      // Special case for layout compositions
      if (updatedElement.type === 'composition' && updatedElement.track >= 90) {
        // Keep the z_index at -1000 for layout compositions
        updatedElement.z_index = -1000;
      } else {
        // Regular elements get z_index matching their track
        updatedElement.z_index = updatedElement.track;
      }
    }

    // Process nested elements recursively
    if (updatedElement.elements && Array.isArray(updatedElement.elements)) {
      updatedElement.elements = updatedElement.elements.map(
        (childElement: any) => {
          if (!childElement) return childElement;

          // Set z_index for child elements
          let updatedChild = { ...childElement };
          if (updatedChild.track !== undefined) {
            updatedChild.z_index = updatedChild.track;
          }

          return updatedChild;
        },
      );
    }

    return updatedElement;
  });

  return {
    ...source,
    elements: updatedElements,
  };
}
